/** Modifier classes for split start/end calendar panels (see component template). */
export const DUAL_PANEL_CALENDAR_START_PANEL_CLASS =
	'picker-panel__calendar-month--start';
export const DUAL_PANEL_CALENDAR_END_PANEL_CLASS =
	'picker-panel__calendar-month--end';

/** Day cell inside the hover range preview while the end date is being picked. */
export const DUAL_PANEL_RANGE_PREVIEW_CLASS = 'picker-panel__day--preview';

export interface DualPanelCalendarHighlightStyleOptions {
	/** CSS selector prefix, e.g. `.cdk-overlay-pane:has(.custom-ckd-container) .picker-panel`. */
	selectorPrefix: string;
	accentColor: string;
	accentColorLight: string;
}

/**
 * Injected overlay styles for dual-calendar range highlighting.
 * Single source of truth — do not duplicate in component SCSS.
 */
export function buildDualPanelCalendarHighlightStyles({
	selectorPrefix,
	accentColor,
	accentColorLight,
}: DualPanelCalendarHighlightStyleOptions): string {
	const p = selectorPrefix;

	return `
      ${p} .mat-calendar-body-cell-content.mat-calendar-body-selected {
        background: var(--picker-accent-color, ${accentColor}) !important;
        color: #fff !important;
      }

      ${p} .mat-calendar-body-in-range::before {
        background: ${accentColorLight} !important;
      }

      ${p} .mat-calendar-body-in-range > .mat-calendar-body-cell-content:not(.mat-calendar-body-selected) {
        background: transparent !important;
      }

      ${p} .mat-calendar-body-cell.${DUAL_PANEL_RANGE_PREVIEW_CLASS}::before {
        background: ${accentColorLight} !important;
      }
    `;
}

const DAY_CELL_SELECTOR = 'mat-month-view .mat-calendar-body-cell';

/** First-of-month dates shown by the start (left) and end (right) panels. */
export interface DualPanelMonths {
	start: Date;
	end: Date;
}

/** Date of an enabled day cell (or an element inside one), from its panel month and day number. */
export function dualPanelDayCellDate(
	element: Element,
	months: DualPanelMonths
): Date | null {
	const cell = element.closest(DAY_CELL_SELECTOR);
	if (!cell || cell.classList.contains('mat-calendar-body-disabled')) {
		return null;
	}
	const month = cell.closest(`.${DUAL_PANEL_CALENDAR_START_PANEL_CLASS}`)
		? months.start
		: cell.closest(`.${DUAL_PANEL_CALENDAR_END_PANEL_CLASS}`)
			? months.end
			: null;
	const day = Number.parseInt(
		cell.querySelector('.mat-calendar-body-cell-content')?.textContent ??
			'',
		10
	);
	if (!month || Number.isNaN(day)) {
		return null;
	}
	return new Date(month.getFullYear(), month.getMonth(), day);
}

/** Marks every day cell from `from` to `to` (inclusive) across both panels. */
export function setDualPanelRangePreview(
	panelRoot: ParentNode,
	from: Date,
	to: Date,
	months: DualPanelMonths
): void {
	panelRoot.querySelectorAll(DAY_CELL_SELECTOR).forEach(cell => {
		const date = dualPanelDayCellDate(cell, months);
		cell.classList.toggle(
			DUAL_PANEL_RANGE_PREVIEW_CLASS,
			!!date && date >= from && date <= to
		);
	});
}

export function clearDualPanelRangePreview(panelRoot: ParentNode): void {
	panelRoot
		.querySelectorAll(`.${DUAL_PANEL_RANGE_PREVIEW_CLASS}`)
		.forEach(cell => cell.classList.remove(DUAL_PANEL_RANGE_PREVIEW_CLASS));
}
